import React, { useEffect, useRef } from "react";
import { Joyride, EVENTS, ACTIONS } from "react-joyride";

/*
  Beginner-friendly tour of The Branding Bar platform.

  Design principles:
  1. Plain English. Assume the user has never used AI tools before.
  2. Cover EVERY sidebar item so nothing feels mysterious.
  3. Mention sub-features inside each page (e.g., Motion Control lives inside
     AI Twin Gallery) so users learn what each room contains.
  4. The user can bail out at any time — skip button, X, Esc, or clicking
     outside the tooltip all close the tour permanently.
*/

const TOUR_STEPS = [
  // ── Intro (not tied to any sidebar item) ──
  {
    target: "body",
    placement: "center",
    title: "Welcome — let's walk the whole app",
    content:
      "This is your AI-powered content studio. In about 2 minutes we'll click through every menu on the left and explain — in plain English — what each one does. You can stop anytime by clicking Skip or the X.",
    disableBeacon: true,
  },

  // ── Core setup flow ──
  {
    target: '[data-testid="nav-brand-kb"]',
    title: "1. Brand Knowledge — START HERE",
    content:
      "Think of this as telling the AI about YOU. What you sell, who you help, your tone of voice, your call-to-action (like \"Comment COHORT\"). Everything the app creates pulls from here, so the more detail you add, the more it sounds like you instead of a generic robot.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-ai-gallery"]',
    title: "2. AI Twin Gallery — your photos",
    content:
      "Upload photos of yourself — these become your \"AI Twin.\" The app uses them to make videos where you appear on camera without you having to actually film. Inside this page you'll also find Outfit Swap (change clothes on a photo) and Motion Control (make any video \"move\" in the style of a reference clip). Upload 5-10 photos for the best variety.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-pipeline"]',
    title: "3. Content Pipeline — your idea board",
    content:
      "Like sticky notes for content. Move ideas through columns: Idea → Script Ready → Recorded → Posted. Use it so you always know what's ready to post tomorrow and what still needs work.",
    placement: "right",
    disableBeacon: true,
  },

  // ── Video creators ──
  {
    target: '[data-testid="nav-auto-create"]',
    title: "4. Auto Create — one-click video",
    content:
      "The magic button. Type a topic (e.g., \"Why your content isn't converting\"), pick one of your photos, hit Generate. The AI writes the script, clones your voice, animates your photo so it looks like you're talking, and spits out a finished 30-60 second video ready to post. No filming, no editing.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-bulk-create"]',
    title: "5. Bulk Create — 15 videos at once",
    content:
      "Same as Auto Create, but for when you want a whole week (or month) of content in one sitting. Pick a count, walk away, come back to 15 finished videos. Great before a launch or vacation.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-auto-agent"]',
    title: "6. Auto Agent — daily autopilot",
    content:
      "Set it and forget it. Pick a time (like 7 AM daily) and how many videos you want made. The app will research trending topics in your niche and generate new content every single day while you sleep. Currently locked to the highest-quality render mode (Talking Photo + Realistic).",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-long-form"]',
    title: "7. Long-Form Studio — YouTube-length videos",
    content:
      "Most AI tools cap at 60 seconds. This doesn't. Type a topic, pick how long (2-30 minutes), and the app writes a full long-form script, renders it in chunks, and stitches them into one YouTube-ready video. Perfect for podcasts-as-video or longer educational content.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-lifestyle"]',
    title: "8. Lifestyle Video — b-roll of you",
    content:
      "Short cinematic clips of your AI Twin in different \"vibes\" — Boss Mode (office), Hustle (grinding late night), Travel, Fitness, etc. No script, no talking — just beautiful b-roll you can layer into other videos or post on its own.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-my-videos"]',
    title: "9. All My Videos — your library",
    content:
      "Every video you've ever created lives here. Download them, save to your Vault, or hit the Motion Remix button to re-animate an old video onto a different AI Twin photo.",
    placement: "right",
    disableBeacon: true,
  },

  // ── Writing tools ──
  {
    target: '[data-testid="nav-linkedin"]',
    title: "10. LinkedIn Engine — LinkedIn posts",
    content:
      "Generates LinkedIn-specific posts (story posts, hot takes, frameworks, myth-busters). You tell it a topic, it writes the post, you tweak and copy-paste. Also has a full weekly content plan generator.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-script"]',
    title: "11. Script Studio — scripts only",
    content:
      "Need just the words, not the video? This writes short-form scripts (TikTok, Reels, Shorts) in your brand voice. Useful if you want to film yourself the old-fashioned way but still want the AI to do the writing.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-hooks"]',
    title: "12. Hook + Caption — the opening line",
    content:
      "The first 3 seconds of a video is everything. This generates high-scroll-stopping hooks AND matching captions. Paste into any of your videos to boost views.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-series"]',
    title: "13. Series Studio — multi-part content",
    content:
      "Plan a content series (\"5 ways to grow your audience — Part 1, 2, 3…\"). The AI keeps the tone consistent across every episode and auto-numbers them.",
    placement: "right",
    disableBeacon: true,
  },

  // ── Reference & storage ──
  {
    target: '[data-testid="nav-vault"]',
    title: "14. Content Vault — your saved content",
    content:
      "A permanent folder where you save the content you want to keep. Caption + hashtags + video — all bundled together so when you're ready to post, everything you need is in one place.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-ai-tea"]',
    title: "15. Industry News — AI Tea Tuesday",
    content:
      "Trending AI news in your industry, auto-curated. Use it to find hot takes to post about without spending hours on Google.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-research"]',
    title: "16. Research Library — content inspiration",
    content:
      "Browse viral post formats, hooks, and frameworks from top creators. A swipe file you can steal (ethically) from for your own content.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-newsletter"]',
    title: "17. Newsletter Intel — email ideas",
    content:
      "Turns any of your existing content into a full email newsletter draft. Great if you send a weekly email but hate writing from scratch.",
    placement: "right",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-account"]',
    title: "18. Credits & Keys — your account",
    content:
      "Check how many credits you have left, upgrade your plan, and connect your own API keys (Fish Audio for voice, muapi.ai for video, Magic Hour for the talking-photo model). Also shows how much money each video actually costs so you can budget. Step-by-step setup instructions included.",
    placement: "right",
    disableBeacon: true,
  },

  // ── Outro ──
  {
    target: "body",
    placement: "center",
    title: "That's the whole menu!",
    content:
      "Best way to start: (1) Fill in Brand Knowledge, (2) Upload 5-10 photos to AI Twin Gallery, (3) Try Auto Create. You'll have your first AI video in about 5 minutes. Need help? Tap \"Help & Pricing\" in the sidebar or join the Discord — link is at the bottom of the menu.",
    disableBeacon: true,
  },
];

const TOUR_STYLES = {
  options: {
    primaryColor: "#E91E8C",
    zIndex: 10000,
    arrowColor: "#fff",
    backgroundColor: "#fff",
    textColor: "#18181b",
    overlayColor: "rgba(0, 0, 0, 0.55)",
    beaconSize: 0,
  },
  tooltipContainer: { textAlign: "left" },
  tooltipTitle: { fontSize: "15px", fontWeight: 700, color: "#E91E8C", marginBottom: "6px" },
  tooltipContent: { fontSize: "13px", lineHeight: "1.55", color: "#3f3f46" },
  buttonNext: { backgroundColor: "#E91E8C", borderRadius: "10px", fontSize: "13px", fontWeight: 600, padding: "8px 18px" },
  buttonBack: { color: "#71717a", fontSize: "13px" },
  buttonSkip: { color: "#E91E8C", fontSize: "12px", fontWeight: 600 },
  buttonClose: { color: "#a1a1aa", width: 14, height: 14, top: 10, right: 10 },
};

// react-joyride still renders an invisible beacon element by default even when
// beaconSize is 0. We replace it with an auto-clicking stub so the tooltip
// appears instantly instead of waiting for the user to hunt for a pulsing dot.
const AutoClickBeacon = React.forwardRef((props, ref) => {
  const btnRef = useRef(null);
  useEffect(() => {
    if (btnRef.current) {
      setTimeout(() => btnRef.current?.click(), 100);
    }
  }, []);
  return (
    <button
      ref={(el) => {
        btnRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      }}
      {...props}
      style={{ opacity: 0, width: 1, height: 1, position: "absolute" }}
    />
  );
});
AutoClickBeacon.displayName = "AutoClickBeacon";

const GuidedTour = ({ run, onFinish }) => {
  // react-joyride v3 renamed `callback` → `onEvent`. Signature: (data, controls).
  // We close the tour (notifying the parent so localStorage gets set) whenever
  // the user hits Skip, the X close button, or reaches the final step.
  const handleEvent = (data /* , controls */) => {
    const { type, action } = data || {};
    const closing =
      type === EVENTS.TOUR_END ||
      action === ACTIONS.CLOSE ||
      action === ACTIONS.SKIP ||
      action === ACTIONS.STOP;
    if (closing && onFinish) {
      onFinish();
    }
  };

  if (!run) return null;

  return (
    <Joyride
      steps={TOUR_STEPS}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableOverlayClose={false}
      disableCloseOnEsc={false}
      hideCloseButton={false}
      spotlightClicks={false}
      beaconComponent={AutoClickBeacon}
      onEvent={handleEvent}
      styles={TOUR_STYLES}
      locale={{
        back: "Back",
        close: "Close",
        last: "Got it",
        next: "Next",
        skip: "Skip tour",
      }}
    />
  );
};

export default GuidedTour;
